import emptyLibraryConnectAccounts1x from '@/assets/images/library/empty-library-connect-accounts/1x.png';
import emptyLibraryConnectAccounts2x from '@/assets/images/library/empty-library-connect-accounts/2x.png';
import emptyLibraryConnectAccounts3x from '@/assets/images/library/empty-library-connect-accounts/3x.png';
import emptyLibraryUploadMedia1x from '@/assets/images/library/empty-library-upload-media/1x.png';
import emptyLibraryUploadMedia2x from '@/assets/images/library/empty-library-upload-media/2x.png';
import emptyLibraryUploadMedia3x from '@/assets/images/library/empty-library-upload-media/3x.png';
import addFacebookProductCatalog1x from '@/assets/images/products/add-facebook-product-catalog/1x.png';
import addFacebookProductCatalog2x from '@/assets/images/products/add-facebook-product-catalog/2x.png';
import addFacebookProductCatalog3x from '@/assets/images/products/add-facebook-product-catalog/3x.png';
import addProductLinks1x from '@/assets/images/products/add-product-links/1x.png';
import addProductLinks2x from '@/assets/images/products/add-product-links/2x.png';
import addProductLinks3x from '@/assets/images/products/add-product-links/3x.png';
import { DEFAULT_CONTENT_TAG_MATCH_TYPE } from '@/components/dashingUI/filterTypes';
import { BRAND, USER } from '@/models/auth/permissions.enum';
import { srcSet } from '@/utils/srcset';

// Single source of truth for the Predicted Performance filter options across
// Library Media, UGC, and Competitor Posts. Keep label and value in sync with
// `getPerformanceParameters` / `predictionLabelToRange` (sc-197870). Lives
// here rather than in `@/utils/vision` because `vision.js` imports the auth
// store at top level — pulling that chain into every consumer of
// `library/constants.js` breaks tests that don't mock `AuthEventTracker`.
export const PREDICTION_TIER_OPTIONS = [
  { label: 'Top', value: 'top' },
  { label: 'Above Average', value: 'high' },
  { label: 'Below Average', value: 'low' },
];

export const LIBRARY_UGC_DRAWERS = {
  SELECT_CONTENT_RIGHTS_TEMPLATE: 'ugcSelectContentRightsTemplate',
  CREATE_CONTENT_RIGHTS_TEMPLATE: 'ugcCreateContentRightsTemplate',
  EDIT_CONTENT_RIGHTS_TEMPLATE: 'ugcEditContentRightsTemplate',
  MANAGE_TERM_DOC_LINKS: 'ugcManageTermDocLinks',
};

export const RIGHTS_DURATION_OPTIONS = [
  { label: '3 Months', value: 90 },
  { label: '6 Months', value: 180 },
  { label: '1 Year', value: 365 },
  { label: '2 Years', value: 730 },
  { label: 'Perpetual', value: 0 },
];

// TODO: Add commands in a follow up ticket.
export const TERM_DOCUMENT_MENU_ITEMS = [
  { icon: 'plus', label: 'Add a Term Document Link' },
  { icon: 'upload', label: 'Upload Term Document' },
];

export const sourceTypeEnum = {
  OWNED: 'OWNED',
  UGC: 'UGC',
  COMPETITIVE: 'OTHER',
  COLLAB: 'COLLAB',
};

export const connectionPlatforms = {
  INSTAGRAM: 'INSTAGRAM',
  INSTAGRAM_INFLUENCER: 'INSTAGRAM_INFLUENCER',
  PINTEREST: 'PINTEREST',
  FACEBOOK: 'FACEBOOK',
  FACEBOOK_ADS: 'FACEBOOK_ADS',
  FACEBOOK_ANALYTICS: 'FACEBOOK_ANALYTICS',
  FACEBOOK_SANDBOX: 'FACEBOOK_SANDBOX',
  GOOGLE_ANALYTICS: 'GOOGLE_ANALYTICS',
  LINKEDIN: 'LINKEDIN',
  TWITTER: 'TWITTER',
  TIKTOK: 'TIKTOK',
  TIKTOK_CREATOR: 'TIKTOK_CREATOR',
  TIKTOK_ADS: 'TIKTOK_ADS',
  TIKTOK_CREATOR_MARKETPLACE: 'TIKTOK_CREATOR_MARKETPLACE',
  YOUTUBE: 'YOUTUBE',
  THREADS: 'THREADS',
  BLUESKY: 'BLUESKY',
  SNAPCHAT: 'SNAPCHAT',
};

const platforms = {
  ...connectionPlatforms,
  UPLOAD: 'UPLOAD',
  EDITED: 'EDITED',
  UGC: 'UGC',
};

export const searchBrandMediaTypes = {
  INSTAGRAM_OWNED: 'INSTAGRAM_OWNED',
  INSTAGRAM_OWNED_BOOSTED: 'INSTAGRAM_OWNED_BOOSTED',
  INSTAGRAM_OWNED_IGTV: 'INSTAGRAM_OWNED_IGTV',
  INSTAGRAM_COLLAB: 'INSTAGRAM_COLLAB',
  INSTAGRAM_UGC: 'INSTAGRAM_UGC',
  INSTAGRAM_UGC_IGTV: 'INSTAGRAM_UGC_IGTV',
  INSTAGRAM_STORY: 'INSTAGRAM_STORY',
  INSTAGRAM_STORY_UGC: 'INSTAGRAM_STORY_UGC',
  LINKEDIN_OWNED: 'LINKEDIN_OWNED',
  PINTEREST_OWNED: 'PINTEREST_OWNED',
  PINTEREST_UGC: 'PINTEREST_UGC',
  FACEBOOK_OWNED: 'FACEBOOK_OWNED',
  TWITTER_OWNED: 'TWITTER_OWNED',
  TIKTOK_OWNED: 'TIKTOK_OWNED',
  TIKTOK_UGC: 'TIKTOK_UGC',
  UPLOADED: 'UPLOADED',
  AI_GENERATED: 'AI_GENERATED',
  YOUTUBE_OWNED: 'YOUTUBE_OWNED',
  YOUTUBE_UGC: 'YOUTUBE_UGC',
  THREADS_OWNED: 'THREADS_OWNED',
  SNAPCHAT_OWNED: 'SNAPCHAT_OWNED',
};

export const brandMediaTypes = {
  ...searchBrandMediaTypes,
  FACEBOOK_TEXT: 'FACEBOOK_TEXT',
  FACEBOOK_LINK: 'FACEBOOK_LINK',
  FACEBOOK_TEXT_LINK: 'FACEBOOK_TEXT_LINK',
  TWITTER_OWNED_LINK: 'TWITTER_OWNED_LINK',
  TWITTER_OWNED_TEXT: 'TWITTER_OWNED_TEXT',
};

export const adsSources = {
  FACEBOOK_ADS: 'FACEBOOK_ADS',
  TIKTOK_AD: 'TIKTOK_AD',
};

export const textOnlySources = {
  FACEBOOK_LINK: 'FACEBOOK_LINK',
  FACEBOOK_TEXT_LINK: 'FACEBOOK_TEXT_LINK',
  FACEBOOK_TEXT: 'FACEBOOK_TEXT',
  FACEBOOK_OTHER_TEXT: 'FACEBOOK_OTHER_TEXT',
  FACEBOOK_OTHER_TL: 'FACEBOOK_OTHER_TL',
  TWITTER_OWNED_LINK: 'TWITTER_OWNED_LINK',
  TWITTER_OWNED_TEXT: 'TWITTER_OWNED_TEXT',
  TWITTER_OTHER_LINK: 'TWITTER_OTHER_LINK',
  TWITTER_OTHER_TEXT: 'TWITTER_OTHER_TEXT',
};

export const visualSources = {
  EDITOR: 'EDITOR',
  FACEBOOK_OWNED: 'FACEBOOK_OWNED',
  FACEBOOK_OTHER: 'FACEBOOK_OTHER',
  INSTAGRAM_COLLAB: 'INSTAGRAM_COLLAB',
  INSTAGRAM_OTHER: 'INSTAGRAM_OTHER',
  INSTAGRAM_OTHER_IGTV: 'INSTAGRAM_OTHER_IGTV',
  INSTAGRAM_OWNED_BOOSTED: 'INSTAGRAM_OWNED_BOOSTED',
  INSTAGRAM_OWNED: 'INSTAGRAM_OWNED',
  INSTAGRAM_OWNED_IGTV: 'INSTAGRAM_OWNED_IGTV',
  INSTAGRAM_STORY: 'INSTAGRAM_STORY',
  INSTAGRAM_STORY_UGC: 'INSTAGRAM_STORY_UGC',
  INSTAGRAM_UGC: 'INSTAGRAM_UGC',
  INSTAGRAM_UGC_IGTV: 'INSTAGRAM_UGC_IGTV',
  LINKEDIN_OWNED: 'LINKEDIN_OWNED',
  PINTEREST_OWNED: 'PINTEREST_OWNED',
  PINTEREST_UGC: 'PINTEREST_UGC',
  TWITTER_OWNED: 'TWITTER_OWNED',
  TWITTER_OTHER: 'TWITTER_OTHER',
  TIKTOK_OWNED: 'TIKTOK_OWNED',
  TIKTOK_UGC: 'TIKTOK_UGC',
  TIKTOK_OTHER: 'TIKTOK_OTHER',
  UPLOADED: 'UPLOADED',
  UPLOAD: 'UPLOAD',
  AI_GENERATED: 'AI_GENERATED',
  AI_GENERATION: 'AI_GENERATION',
  YOUTUBE_OWNED: 'YOUTUBE_OWNED',
  YOUTUBE_OTHER: 'YOUTUBE_OTHER',
  THREADS_OWNED: 'THREADS_OWNED',
  SNAPCHAT_OWNED: 'SNAPCHAT_OWNED',
  SNAPCHAT_OWNED_SNAP: 'SNAPCHAT_OWNED_SNAP',
  SNAPCHAT_OWNED_STORY: 'SNAPCHAT_OWNED_STORY',
  SNAPCHAT_OWNED_SPOTLIGHT: 'SNAPCHAT_OWNED_SPOTLIGHT',
  SNAPCHAT_OWNED_SAVED_STORY: 'SNAPCHAT_OWNED_SAVED_STORY',
};

// Visual source types queried for similar-media lookups in the media preview
// drawers. FACEBOOK_OWNED is appended conditionally by callers with FB insights.
export const SIMILAR_MEDIA_SOURCE_TYPES = [
  visualSources.UPLOADED,
  visualSources.AI_GENERATED,
  visualSources.INSTAGRAM_OWNED,
  visualSources.INSTAGRAM_OWNED_IGTV,
  visualSources.INSTAGRAM_UGC,
  visualSources.INSTAGRAM_COLLAB,
  visualSources.INSTAGRAM_STORY,
  visualSources.PINTEREST_OWNED,
  visualSources.LINKEDIN_OWNED,
  visualSources.SNAPCHAT_OWNED_STORY,
  visualSources.SNAPCHAT_OWNED_SAVED_STORY,
  visualSources.SNAPCHAT_OWNED_SPOTLIGHT,
];

export const sources = {
  ...adsSources,
  ...textOnlySources,
  ...visualSources,
};

// Name of source filters are used in many places as platform types.
// And it doesn't work well for Instagram story type.
// Declare one constant here to make it a bit more clear what values it takes for stories.
// Hopefully will help testing easier when we do a bigger refactor in SourcesFilter.vue.
export const sourcesFilterNames = {
  SOURCE_FILTER_INSTAGRAM: 'instagram',
  SOURCE_FILTER_INSTAGRAM_STORIES: 'instagram stories',
  SOURCE_FILTER_TIKTOK: 'tikTok',
  SOURCE_FILTER_YOUTUBE: 'youTube',
  SOURCE_FILTER_LINKEDIN: 'linkedIn',
  SOURCE_FILTER_TWITTER: 'twitter',
  SOURCE_FILTER_PINTEREST: 'pinterest',
  SOURCE_FILTER_FACEBOOK: 'facebook',
  SOURCE_FILTER_THREADS: 'threads',
  SOURCE_FILTER_SNAPCHAT: 'snapchat',
};

export const sourceLists = {
  FACEBOOK_LINK_LIST: ['FACEBOOK_LINK', 'FACEBOOK_TEXT_LINK'],
  FACEBOOK_GALLERY_LIST: ['FACEBOOK_OWNED', 'FACEBOOK_LINK'],
  FACEBOOK_SOURCE_LIST: [
    'FACEBOOK',
    'FACEBOOK_OWNED',
    'FACEBOOK_TEXT',
    'FACEBOOK_LINK',
    'FACEBOOK_TEXT_LINK',
    'FACEBOOK_OTHER',
    'FACEBOOK_OTHER_TEXT',
    'FACEBOOK_OTHER_LINK',
    'FACEBOOK_OTHER_TL',
  ],
  FACEBOOK_COMPETITIVE_SOURCE_LIST: [
    'FACEBOOK_OTHER',
    'FACEBOOK_OTHER_TEXT',
    'FACEBOOK_OTHER_LINK',
    'FACEBOOK_OTHER_TL',
  ],
  FACEBOOK_SOURCE_LIST_V1: [
    'FACEBOOK_OWNED',
    'FACEBOOK_TEXT',
    'FACEBOOK_LINK',
    'FACEBOOK_TEXT_LINK',
  ],
  FACEBOOK_OWNED_SOURCE_LIST: [
    'FACEBOOK_OWNED',
    'FACEBOOK_TEXT',
    'FACEBOOK_LINK',
    'FACEBOOK_TEXT_LINK',
  ],
  INSTAGRAM_SOURCE_LIST: [
    'INSTAGRAM_OWNED',
    'INSTAGRAM_OWNED_IGTV',
    'INSTAGRAM_UGC',
    'INSTAGRAM_OTHER',
  ],
  INSTAGRAM_STORY_SOURCE_LIST: ['INSTAGRAM_STORY', 'INSTAGRAM_STORY_UGC'],
  INSTAGRAM_OWNED_SOURCE_LIST: ['INSTAGRAM_OWNED', 'INSTAGRAM_OWNED_IGTV', 'INSTAGRAM_STORY'],
  PINTEREST_SOURCE_LIST: ['PINTEREST_OWNED'],
  PINTEREST_OWNED_SOURCE_LIST: ['PINTEREST_OWNED'],
  THREADS_OWNED_SOURCE_LIST: ['THREADS_OWNED'],
  LINKEDIN_OWNED_SOURCE_LIST: ['LINKEDIN_OWNED'],
  TWITTER_SOURCE_LIST: [
    'TWITTER_OWNED',
    'TWITTER_OWNED_LINK',
    'TWITTER_OWNED_TEXT',
    'TWITTER',
    'TWITTER_LINK',
    'TWITTER_TEXT',
    'TWITTER_OTHER',
    'TWITTER_OTHER_LINK',
    'TWITTER_OTHER_TEXT',
  ],
  TWITTER_COMPETITIVE_SOURCE_LIST: ['TWITTER_OTHER', 'TWITTER_OTHER_LINK', 'TWITTER_OTHER_TEXT'],
  TWITTER_SOURCE_LIST_V1: ['TWITTER_OWNED', 'TWITTER_OWNED_LINK', 'TWITTER_OWNED_TEXT'],
  TWITTER_OWNED_SOURCE_LIST: ['TWITTER_OWNED', 'TWITTER_OWNED_LINK', 'TWITTER_OWNED_TEXT'],
  TWITTER_LINK_LIST: ['TWITTER_LINK', 'TWITTER_OWNED_LINK'],
  TIKTOK_SOURCE_LIST: ['TIKTOK_OWNED'],
  TIKTOK_OWNED_SOURCE_LIST: ['TIKTOK_OWNED'],
  YOUTUBE_SOURCE_LIST: ['YOUTUBE_OWNED'],
  YOUTUBE_OWNED_SOURCE_LIST: ['YOUTUBE_OWNED'],
  YOUTUBE_COMPETITIVE_SOURCE_LIST: ['YOUTUBE_OTHER'],
  SNAPCHAT_SOURCE_LIST: [
    'SNAPCHAT_OWNED',
    'SNAPCHAT_OWNED_SNAP',
    'SNAPCHAT_OWNED_STORY',
    'SNAPCHAT_OWNED_SPOTLIGHT',
    'SNAPCHAT_OWNED_SAVED_STORY',
  ],
  NO_MEDIA_SOURCE_LIST: [
    'TWITTER_OWNED_LINK',
    'TWITTER_OWNED_TEXT',
    'FACEBOOK_TEXT',
    'FACEBOOK_TEXT_LINK',
  ],
  get OWNED_LIST() {
    return [
      ...this.FACEBOOK_OWNED_SOURCE_LIST,
      ...this.INSTAGRAM_OWNED_SOURCE_LIST,
      ...this.PINTEREST_OWNED_SOURCE_LIST,
      ...this.THREADS_OWNED_SOURCE_LIST,
      ...this.LINKEDIN_OWNED_SOURCE_LIST,
      ...this.TIKTOK_OWNED_SOURCE_LIST,
      ...this.TWITTER_OWNED_SOURCE_LIST,
      ...this.YOUTUBE_OWNED_SOURCE_LIST,
    ];
  },
  UGC_LIST: ['INSTAGRAM_UGC', 'PINTEREST_UGC', 'UGC', 'INSTAGRAM_STORY_UGC', 'TIKTOK_UGC'],
  UGC_SOURCE_LIST: ['INSTAGRAM_UGC', 'PINTEREST_UGC', 'INSTAGRAM_STORY_UGC'],
  COMPETITIVE_LIST: [
    'INSTAGRAM_OTHER',
    'INSTAGRAM_OTHER_IGTV',
    'OTHER',
    'TWITTER_OTHER',
    'TWITTER_OTHER_LINK',
    'TWITTER_OTHER_TEXT',
    'YOUTUBE_OTHER',
    'FACEBOOK_OTHER',
    'FACEBOOK_OTHER_TEXT',
    'FACEBOOK_OTHER_LINK',
    'FACEBOOK_OTHER_TL',
  ],
};

export const visionPredictedSources = [
  visualSources.EDITOR,
  visualSources.UPLOAD,
  visualSources.UPLOADED,
  visualSources.AI_GENERATION,
  visualSources.AI_GENERATED,
  sourceTypeEnum.COMPETITIVE,
  ...sourceLists.UGC_SOURCE_LIST,
  visualSources.TIKTOK_UGC,
];

export const invalidVisionUgcSources = ['TIKTOK'];

const postsCsvTypes = {
  POSTS_CSV_LINK: 'links',
  POSTS_CSV_IMAGE: 'images',
  POSTS_CSV_POST: 'posts',
};

export const mediaTypes = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
};

export const linkedinMediaTypes = {
  DOCUMENT: 'DOCUMENT',
  IMAGE: 'IMAGE',
  LINK: 'LINK',
  MULTI_IMAGE: 'MULTI_IMAGE',
  TEXT: 'TEXT',
  VIDEO: 'VIDEO',
};

export const threadsMediaTypes = {
  TEXT_POST: 'TEXT_POST',
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  CAROUSEL_ALBUM: 'CAROUSEL_ALBUM',
  AUDIO: 'AUDIO',
};

export const instagramPostTypes = {
  IMAGE: 'image',
  VIDEO: 'video',
  REEL: 'reel',
  CAROUSEL: 'carousel',
};

export const pinterestPostTypes = {
  IMAGE: 'image',
  VIDEO: 'video',
  CAROUSEL: 'carousel',
};

export const tiktokMediaTypes = {
  PHOTO: 'PHOTO',
  VIDEO: 'VIDEO',
};

export const snapchatMediaTypes = {
  STORY: 'STORY',
  SPOTLIGHT: 'SPOTLIGHT',
  SAVED_STORY: 'SAVED_STORY',
  SNAP: 'SNAP',
};

export const aspectRatios = {
  SQUARE: 'SQUARE',
  LANDSCAPE: 'LANDSCAPE',
  PORTRAIT: 'PORTRAIT',
};

export const visualSearchImageTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];

export const mediaBadgeEnumsByPlatform = {
  [searchBrandMediaTypes.LINKEDIN_OWNED]: [
    {
      mediaType: linkedinMediaTypes.VIDEO,
      tooltip: 'This video post is published on your LinkedIn Page',
      iconName: 'video',
      showIcon: false,
    },
    {
      mediaType: linkedinMediaTypes.DOCUMENT,
      tooltip: 'This document post is published on your LinkedIn Page',
      iconName: 'document',
      showIcon: false,
    },
    {
      mediaType: linkedinMediaTypes.IMAGE,
      tooltip: 'This image post is published on your LinkedIn Page',
      iconName: 'image',
      showIcon: false,
    },
    {
      mediaType: linkedinMediaTypes.MULTI_IMAGE,
      tooltip: 'This carousel post is published on your LinkedIn Page',
      iconName: 'multipleMedia',
      showIcon: false,
    },
    {
      mediaType: linkedinMediaTypes.TEXT,
      tooltip: 'This text post is published on your LinkedIn Page',
      iconName: 'text',
      showIcon: false,
    },
    {
      mediaType: linkedinMediaTypes.LINK,
      tooltip: 'This link post is published on your LinkedIn Page',
      iconName: 'link',
      showIcon: false,
    },
  ],
  [searchBrandMediaTypes.THREADS_OWNED]: [
    {
      mediaType: threadsMediaTypes.TEXT_POST,
      tooltip: 'This text post is published on your Threads Profile',
      iconName: 'text',
      showIcon: false,
    },
    {
      mediaType: threadsMediaTypes.IMAGE,
      tooltip: 'This image post is published on your Threads Profile',
      iconName: 'image',
      showIcon: false,
    },
    {
      mediaType: threadsMediaTypes.VIDEO,
      tooltip: 'This video post is published on your Threads Profile',
      iconName: 'video',
      showIcon: false,
    },
    {
      mediaType: threadsMediaTypes.CAROUSEL_ALBUM,
      tooltip: 'This carousel post is published on your Threads Profile',
      iconName: 'multipleMedia',
      showIcon: false,
    },
    {
      mediaType: threadsMediaTypes.AUDIO,
      tooltip: 'This audio post is published on your Threads Profile',
      iconName: 'music-note',
      showIcon: false,
    },
  ],
  [searchBrandMediaTypes.SNAPCHAT_OWNED]: [
    {
      mediaType: snapchatMediaTypes.STORY,
      iconName: 'snap-story',
      showIcon: false,
    },
    {
      mediaType: snapchatMediaTypes.SPOTLIGHT,
      iconName: 'snap-spotlight',
      showIcon: false,
    },
    {
      mediaType: snapchatMediaTypes.SAVED_STORY,
      iconName: 'bookmark-check',
      showIcon: false,
    },
  ],
};

// deprecated constant, use availabilityStockTypes instead
const productStockTypes = {
  IN_STOCK: 'in stock',
  OUT_OF_STOCK: 'out of stock',
  PRODUCT_TYPE: {
    IN_STOCK: 'in-stock',
    ALL: 'all',
  },
  PREORDER: 'preorder',
  AVAILABLE_FOR_ORDER: 'available for order',
  DISCONTINUED: 'discontinued',
};

export const productAvailabilityTypes = {
  IN_STOCK: 'in stock',
  OUT_OF_STOCK: 'out of stock',
  PREORDER: 'preorder',
  AVAILABLE_FOR_ORDER: 'available for order',
  DISCONTINUED: 'discontinued',
};

export const PRODUCT_CSV_AVAILABILITY_VALUES = Object.values(productAvailabilityTypes);

const AVAILABILITY_VARIATIONS = {
  [productAvailabilityTypes.IN_STOCK]: [
    productAvailabilityTypes.IN_STOCK,
    'in-stock',
    'in stock (checking)',
  ],
  [productAvailabilityTypes.OUT_OF_STOCK]: [productAvailabilityTypes.OUT_OF_STOCK],
  [productAvailabilityTypes.PREORDER]: [productAvailabilityTypes.PREORDER],
  [productAvailabilityTypes.AVAILABLE_FOR_ORDER]: [productAvailabilityTypes.AVAILABLE_FOR_ORDER],
  [productAvailabilityTypes.DISCONTINUED]: [productAvailabilityTypes.DISCONTINUED],
};

const normalizeAvailabilityVariation = (value) => value.replace(/[\s_-]+/g, '').toLowerCase();

const normalizedAvailabilityVariations = new Map(
  Object.entries(AVAILABILITY_VARIATIONS).flatMap(([availability, variations]) =>
    variations.map((variation) => [normalizeAvailabilityVariation(variation), availability]),
  ),
);

export const normalizeProductAvailability = (value) =>
  value
    ? (normalizedAvailabilityVariations.get(normalizeAvailabilityVariation(value)) ?? null)
    : null;

export const productAvailabilityLabels = {
  IN_STOCK: 'In Stock',
  OUT_OF_STOCK: 'Out of Stock',
  PREORDER: 'Pre-order',
  AVAILABLE_FOR_ORDER: 'Available for Order',
  DISCONTINUED: 'Discontinued',
};

export const productAvailabilityMap = Object.keys(productAvailabilityTypes).reduce((acc, key) => {
  acc[productAvailabilityTypes[key]] = productAvailabilityLabels[key];
  return acc;
}, {});

export const productCardAvailabilityLabels = {
  [productAvailabilityTypes.IN_STOCK]: {
    label: productAvailabilityLabels.IN_STOCK,
    style: 'availability-green',
  },
  [productAvailabilityTypes.OUT_OF_STOCK]: {
    label: productAvailabilityLabels.OUT_OF_STOCK,
    style: 'availability-red',
  },
  [productAvailabilityTypes.AVAILABLE_FOR_ORDER]: {
    label: productAvailabilityLabels.AVAILABLE_FOR_ORDER,
    style: 'availability-blue',
  },
  [productAvailabilityTypes.PREORDER]: {
    label: productAvailabilityLabels.PREORDER,
    style: 'availability-blue',
  },
  [productAvailabilityTypes.DISCONTINUED]: {
    label: productAvailabilityLabels.DISCONTINUED,
    style: 'availability-grey',
  },
};

/** @type {Record<string, import('@dashhudson/dashing-ui/chip').ChipProps>} */
export const productCardAvailabilityChips = {
  [productAvailabilityTypes.IN_STOCK]: {
    label: productAvailabilityLabels.IN_STOCK,
    severity: 'success',
  },
  [productAvailabilityTypes.AVAILABLE_FOR_ORDER]: {
    label: productAvailabilityLabels.AVAILABLE_FOR_ORDER,
    severity: 'success',
  },
  [productAvailabilityTypes.PREORDER]: {
    label: productAvailabilityLabels.PREORDER,
    severity: 'success',
  },
  [productAvailabilityTypes.DISCONTINUED]: {
    label: productAvailabilityLabels.DISCONTINUED,
    severity: 'warning',
  },
  [productAvailabilityTypes.OUT_OF_STOCK]: {
    label: productAvailabilityLabels.OUT_OF_STOCK,
    severity: 'danger',
  },
};

export const productReviewStatusTypes = {
  APPROVED: 'approved',
  OUTDATED: 'outdated',
  PENDING: 'pending',
  REJECTED: 'rejected',
};

export const productReviewStatusLabels = {
  APPROVED: 'Approved',
  OUTDATED: 'Outdated',
  PENDING: 'Pending',
  REJECTED: 'Rejected',
};

export const scales = {
  HOURLY: 'HOURLY',
  DAILY: 'DAILY',
  MONTHLY: 'MONTHLY',
};

const sortingOptions = {
  MOST_RECENT: '-CREATED',
  MOST_ENGAGEMENT: '-INSTAGRAM_ENGAGEMENT',
  MOST_EFFECTIVE: '-INSTAGRAM_EFFECTIVENESS',
  INSTAGRAM_REACH: '-INSTAGRAM_REACH',
  LIBRARY_TRAFFIC: '-LIBRARY_TRAFFIC',
  STORY_REACH: '-IG_STORY_AVG_REACH',
  STORY_IMPRESSIONS: '-IG_STORY_AVG_IMPRESSIONS',
  STORY_EXIT_RATE: '-IG_STORY_AVG_EXIT_RATE',
};

export const contentRightsPostTypes = {
  ALL: 'ALL',
  APPROVED: 'APPROVED',
  EXPIRED: 'EXPIRED',
  FAILED: 'FAILED',
  NOT_REQUESTED: 'NOT_REQUESTED',
  LAPSED: 'LAPSED',
  RESET: 'RESET',
  ORIGINAL_TYPE: 'REQUEST',
  REQUESTED: 'REQUESTED',
  REQUEST_TIME_FORMAT: 'MMM D, YYYY h:mma',
};

const durationUnits = {
  SECONDS: 'SECONDS',
  MILLISECONDS: 'MILLISECONDS',
};

const creatorSources = {
  CREATOR_INSTAGRAM: `CREATORS_INSTAGRAM`,
  CREATOR_INSTAGRAM_STORIES: 'CREATORS_INSTAGRAM_STORIES',
};

export default {
  ...platforms,
  ...sources,
  ...sourceLists,
  ...sourcesFilterNames,
  // source type
  OWNED: 'OWNED',
  ...mediaTypes,
  ...postsCsvTypes,
  ...productStockTypes,
  ...scales,
  ...sortingOptions,
  ...contentRightsPostTypes,
  ...aspectRatios,
  ...durationUnits,
  ...creatorSources,
  ...searchBrandMediaTypes,
};

export const productFeedSortEnum = {
  ARCHIVED: 'ARCHIVED',
};

export const productImportSourceEnum = {
  FACEBOOK_PRODUCT_CATALOG: 'FACEBOOK_PRODUCT_CATALOG',
  OTHER_PRODUCT_FEED: 'OTHER_PRODUCT_FEED',
  SINGLE_PRODUCT_LINK: 'SINGLE_PRODUCT_LINK',
};

export const productCatalogSourceEnum = {
  FACEBOOK: 'FACEBOOK',
};

export const PRODUCT_CSV_FIELDS = {
  REQUIRED: [
    { key: 'url', name: 'Product URL', aliases: ['link', 'product url'] },
    { key: 'title', name: 'Product Name', aliases: ['title', 'product name'] },
  ],
  DETAILS: [
    { key: 'source_id', name: 'Product ID', aliases: ['id', 'product id'] },
    { key: 'source_group_id', name: 'Group ID', aliases: ['item_group_id', 'source_group_id'] },
    { key: 'image_url', name: 'Product Image URL', aliases: ['image_link', 'product image url'] },
    { key: 'description', name: 'Description', aliases: ['description', 'product description'] },
    { key: 'price', name: 'Price', aliases: [] },
    { key: 'currency_code', name: 'Currency Code', aliases: ['currency code', 'currency'] },
    { key: 'availability', name: 'Availability', aliases: ['availability'] },
    { key: 'category', name: 'Category', aliases: [] },
  ],
};

export const productFeedStatus = {
  INIT: 'INIT',
  CONNECTED: 'CONNECTED',
  UPDATING: 'UPDATING',
  ERROR: 'ERROR',
};

export const productFeedTypeEnum = {
  PRODUCT_CATALOG: 'PRODUCT_CATALOG',
};

export const productImportSourceMap = {
  'facebook-product-catalog': 'Facebook Product Catalog',
  'single-product-link': 'Single Product Link',
  'other-product-feed': 'Custom Product Feeds',
};

export const productFeedInfoMessage =
  'You can now import products by adding a Facebook Product Catalog. If you would like to import products with a custom Product Feed link, please contact your Account Representative.';

export const archiveFeedConfirmationTitle = 'Archive Product Feed?';

export const archiveFeedConfirmationMessage =
  'Archiving this Product Feed will archive all associated products. Archived products cannot be used to add links to media and will be hidden from your products. You can still see the analytics by toggling "Show archived". To access these products please un-archive the Product Feed.';

export const unarchiveFeedConfirmationTitle = 'Unarchive Product Feed?';

export const unarchiveFeedConfirmationMessage =
  'Unarchiving your Product Feed will re-import your products. Products will be available for tagging immediately, however, it may take up to 24 hours for all properties to update.';

export const productCatalogAlreadyImportedTooltip =
  "You've already imported this\nFacebook Product Catalog";

export const productFeedCreatedNotification =
  "We've started importing your Facebook Product Catalog. This may take up to 24 hours.";

export const connectFacebookPlatformPopupMessage =
  'To import your Facebook Product Catalog and access Facebook and Instagram insights you must connect your Facebook account to Dash Social';

export const connectFacebookPlatformPopupTitle = 'Connect Your Facebook Account';

export const authorizeProductCatalogsPopupMessage =
  'To import your Facebook Product Catalog you must authorize access with Dash Social';

export const authorizeProductCatalogsPopupTitle = 'Connect Your Facebook Product Catalog';

export const productFeedArchived = 'ARCHIVED';

export const productFeedStatusTooltips = {
  [productFeedStatus.CONNECTED]:
    'Your Product Feed is connected and will refresh daily. New products added may take up to 24 hours to appear.',
  [productFeedStatus.ERROR]:
    'Error importing your Product Feed. Please reach out to your Customer Success Representative.',
  [productFeedStatus.UPDATING]:
    "We're updating your Product Feed. When the update is complete the status will change to Connected.",
  [productFeedArchived]: 'This Product Feed is archived.',
};

export const productSortOptions = [
  { label: 'Recently Added', value: 'DATE' },
  { label: 'Total Clicks', value: 'CLICKS' },
];

export const productSearchPlaceholder = 'Search Name or ID';
export const productSearchTitlePlaceholder = 'Enter Name';
export const productSearchSourceIdPlaceholder = 'Enter Product ID';
export const productSearchSourceGroupIdPlaceholder = 'Enter Group ID';

export const productSearchScopeTooltips = {
  partialMatch: 'Broad search, can fail on large catalogs',
  exactMatch: 'Exact match only, faster and more reliable for large catalogs',
};

export const PRODUCT_ACTION_BANNERS = Object.freeze([
  {
    id: 'addFacebookProductCatalog',
    title: 'Import Your Product Feed',
    message:
      'Connect your Facebook Product Catalog or contact us to import a custom product feed, making your products easily shoppable.',
    bannerSrc: addFacebookProductCatalog1x,
    bannerSrcset: srcSet(
      addFacebookProductCatalog1x,
      addFacebookProductCatalog2x,
      addFacebookProductCatalog3x,
    ),
    ctaButton: { label: 'Add Facebook Product Catalog', 'icon-right': 'plus' },
  },
  {
    id: 'addProductLinks',
    title: 'Add Product Links',
    message:
      "Add links for use with Dash's EComm features, including product pages, blog posts and website pages.",
    bannerSrc: addProductLinks1x,
    bannerSrcset: srcSet(addProductLinks1x, addProductLinks2x, addProductLinks3x),
    ctaButton: { label: 'Add Product Links', 'icon-right': 'plus' },
  },
]);

export const multiSelectTopBarArchiveConfirmationCopy = {
  message:
    'Archiving products will remove them from your products and you will no longer be able to access them when scheduling media. View archived products by toggling "Show archived".',
  title: 'Archive product',
};

export const fbGraphApiErrors = {
  PERMISSIONS_ERROR: 'PERMISSIONS_ERROR',
  API_PERMISSION_ERROR: 'API_PERMISSION_ERROR',
  INVALID_PARAMETER: 'INVALID_PARAMETER',
};

/** @type {import('@dashhudson/dashing-ui/content-banner').ContentBannerProps} */
export const productsNoResultFoundState = {
  center: true,
  variant: 'sectionEmpty',
  icon: 'search',
  title: "Couldn't Find a Match",
  message: 'Review or adjust your filters or search to uncover more results',
  ctaButton: { label: 'Reset All', severity: 'tertiary' },
};

/** @type {import('@dashhudson/dashing-ui/content-banner').ContentBannerProps} */
export const allProductsArchivedEmptyState = {
  center: true,
  variant: 'sectionEmpty',
  icon: 'product-catalog',
  title: 'Only Archived Products Exist',
  message: 'Add new products or display archived products from the filter drawer',
  ctaButton: { label: 'Add Products', severity: 'tertiary' },
};

export const audienceSectionTabs = {
  TRAFFIC_SOURCE: 'TRAFFIC_SOURCE',
  LOCATION: 'LOCATION',
  DEMOGRAPHICS: 'DEMOGRAPHICS',
  VIEWER_TYPES: 'VIEWER_TYPES',
};

export const audienceSectionTabLabels = {
  [audienceSectionTabs.TRAFFIC_SOURCE]: 'Traffic Source',
  [audienceSectionTabs.LOCATION]: 'Location',
  [audienceSectionTabs.DEMOGRAPHICS]: 'Demographics',
  [audienceSectionTabs.VIEWER_TYPES]: 'Viewer Types',
};

export const MAX_MEDIA_OFFSET = 10000;

export const MEDIA_TYPE_OPTIONS = {
  IMAGE: {
    value: 'IMAGE',
    title: 'Photos',
    tooltip: 'Image',
  },
  VIDEO: {
    value: 'VIDEO',
    title: 'Videos',
    tooltip: 'Video',
  },
  LINK: {
    value: 'LINK',
    title: 'Links',
    tooltip: 'Link',
  },
  TEXT: {
    value: 'TEXT',
    title: 'Text',
    tooltip: 'Text',
  },
};

export const ugcMediaDeleteMessage =
  'Deleting a UGC post only removes it from Organic Reach and Library within Dash Social. Are you sure you want to delete this ';
export const nonUGCMediaDeleteMessage = 'Are you sure you want to delete this ';

export const bulkUploadNoBrandTagsMessage =
  'Your brand(s) do not have any Brand Tags yet. Go to Settings to create new Brand Tags.';

export const allowedMediaTypes = Object.freeze([
  connectionPlatforms.FACEBOOK,
  connectionPlatforms.FACEBOOK_ANALYTICS,
  connectionPlatforms.FACEBOOK_SANDBOX,
  connectionPlatforms.INSTAGRAM,
  connectionPlatforms.INSTAGRAM_INFLUENCER,
  connectionPlatforms.LINKEDIN,
  connectionPlatforms.PINTEREST,
  connectionPlatforms.TIKTOK,
  connectionPlatforms.TIKTOK_ADS,
  connectionPlatforms.TWITTER,
  connectionPlatforms.YOUTUBE,
  sources.EDITOR,
  sources.FACEBOOK_ADS,
  sources.FACEBOOK_OTHER,
  sources.FACEBOOK_OTHER_TEXT,
  sources.FACEBOOK_OTHER_TL,
  sources.FACEBOOK_OWNED,
  sources.INSTAGRAM_COLLAB,
  sources.INSTAGRAM_OTHER,
  sources.INSTAGRAM_OTHER_IGTV,
  sources.INSTAGRAM_OWNED,
  sources.INSTAGRAM_OWNED_BOOSTED,
  sources.INSTAGRAM_OWNED_IGTV,
  sources.INSTAGRAM_STORY,
  sources.INSTAGRAM_STORY_UGC,
  sources.INSTAGRAM_UGC,
  sources.INSTAGRAM_UGC_IGTV,
  sources.LINKEDIN_OWNED,
  sources.PINTEREST_OWNED,
  sources.PINTEREST_UGC,
  sources.TIKTOK_UGC,
  sources.TIKTOK_OTHER,
  sources.TWITTER_OTHER,
  sources.TWITTER_OWNED,
  sources.UPLOAD,
  sources.UPLOADED,
  sources.AI_GENERATED,
  sources.YOUTUBE_OTHER,
  sources.YOUTUBE_OWNED,
  sources.SNAPCHAT_OWNED,
  sources.SNAPCHAT_OWNED_SNAP,
  sources.SNAPCHAT_OWNED_STORY,
  sources.SNAPCHAT_OWNED_SAVED_STORY,
  sources.SNAPCHAT_OWNED_SPOTLIGHT,
  sourceTypeEnum.UGC,
]);

export const MEDIA_SOURCES_OWNED_FILTERS = Object.freeze({
  TIKTOK_OWNED: {
    channel: 'tiktok',
    chipInputLabel: 'TikTok Owned',
    label: 'TikTok',
    permissions: BRAND.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS,
    value: 'TIKTOK_OWNED',
    sources: [sources.TIKTOK_OWNED],
  },
  INSTAGRAM_OWNED: {
    channel: 'instagram',
    chipInputLabel: 'Instagram Owned',
    label: 'Instagram',
    value: 'INSTAGRAM_OWNED',
    sources: [sources.INSTAGRAM_OWNED, sources.INSTAGRAM_OWNED_IGTV],
  },
  INSTAGRAM_STORY_OWNED: {
    channel: 'instagram story',
    chipInputLabel: 'Instagram Stories Owned',
    label: 'Instagram Stories',
    value: 'INSTAGRAM_STORY_OWNED',
    sources: [sources.INSTAGRAM_STORY],
  },
  FACEBOOK_OWNED: {
    channel: 'facebook',
    chipInputLabel: 'Facebook Owned',
    label: 'Facebook',
    permissions: BRAND.FACEBOOK.CAN_ACCESS_FB_INSIGHTS,
    value: 'FACEBOOK_OWNED',
    sources: sourceLists.FACEBOOK_GALLERY_LIST,
  },
  PINTEREST_OWNED: {
    channel: 'pinterest',
    chipInputLabel: 'Pinterest Owned',
    label: 'Pinterest',
    value: 'PINTEREST_OWNED',
    sources: [sources.PINTEREST_OWNED],
  },
  TWITTER_OWNED: {
    channel: 'x',
    chipInputLabel: 'X Owned',
    label: 'X',
    permissions: BRAND.TWITTER.CAN_ACCESS_TWITTER_INSIGHTS,
    value: 'TWITTER_OWNED',
    sources: [sources.TWITTER_OWNED],
  },
  YOUTUBE_OWNED: {
    channel: 'youtube',
    chipInputLabel: 'YouTube Owned',
    label: 'YouTube',
    permissions: BRAND.YOUTUBE.CAN_ACCESS_YOUTUBE_INSIGHTS,
    value: 'YOUTUBE_OWNED',
    sources: [sources.YOUTUBE_OWNED],
  },
  LINKEDIN_OWNED: {
    channel: 'linkedin',
    label: 'LinkedIn',
    chipInputLabel: 'LinkedIn Owned',
    permissions: BRAND.LINKEDIN.CAN_ACCESS_LINKEDIN_INSIGHTS,
    value: 'LINKEDIN_OWNED',
    sources: [sources.LINKEDIN_OWNED],
  },
  THREADS_OWNED: {
    channel: 'threads',
    label: 'Threads',
    chipInputLabel: 'Threads Owned',
    permissions: BRAND.THREADS.CAN_ACCESS_THREADS_INSIGHTS,
    value: 'THREADS_OWNED',
    sources: [sources.THREADS_OWNED],
  },
  SNAPCHAT_OWNED: {
    channel: 'snapchat',
    label: 'Snapchat',
    chipInputLabel: 'Snapchat Owned',
    permissions: BRAND.SNAPCHAT.CAN_ACCESS_SNAPCHAT_INSIGHTS,
    value: 'SNAPCHAT_OWNED',
    sources: [
      sources.SNAPCHAT_OWNED_STORY,
      sources.SNAPCHAT_OWNED_SPOTLIGHT,
      sources.SNAPCHAT_OWNED_SAVED_STORY,
    ],
  },
});

export const MEDIA_SOURCES_UGC_FILTERS = Object.freeze({
  TIKTOK_UGC: {
    channel: 'tiktok',
    chipInputLabel: 'TikTok UGC',
    label: 'TikTok',
    permissions: [
      USER.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS,
      BRAND.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS,
      BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE,
      USER.INSTAGRAM.CAN_ACCESS_UGC_MORE,
    ],
    value: 'TIKTOK_UGC',
    sources: [sources.TIKTOK_UGC],
  },
  INSTAGRAM_UGC: {
    channel: 'instagram',
    chipInputLabel: 'Instagram UGC',
    label: 'Instagram',
    permissions: [BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE, USER.INSTAGRAM.CAN_ACCESS_UGC_MORE],
    value: 'INSTAGRAM_UGC',
    sources: [sources.INSTAGRAM_UGC],
  },
  INSTAGRAM_STORY_UGC: {
    channel: 'instagram story',
    label: 'Instagram Stories',
    chipInputLabel: 'Instagram Stories UGC',
    permissions: [BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE, USER.INSTAGRAM.CAN_ACCESS_UGC_MORE],
    value: 'INSTAGRAM_STORY_UGC',
    sources: [sources.INSTAGRAM_STORY_UGC],
  },
});

export const MEDIA_SOURCES_FILTER_OPTIONS = Object.freeze({
  ...MEDIA_SOURCES_OWNED_FILTERS,
  ...MEDIA_SOURCES_UGC_FILTERS,
  UPLOADED: {
    label: 'Uploads',
    chipInputLabel: 'Uploads',
    value: 'UPLOADED',
    sources: [sources.UPLOADED],
  },
});

export const PLATFORM_MEDIA_TYPES = Object.freeze({
  IMAGE: 'IMAGE',
  CAROUSEL: 'CAROUSEL',
  VIDEO: 'VIDEO',
  LIVE: 'LIVE',
  ON_DEMAND: 'ON_DEMAND',
  SHORT: 'SHORT',
  STORY: 'STORY',
  STORY_FRAME: 'STORY_FRAME',
  REEL: 'REEL',
  AUDIO: 'AUDIO',
  LINK: 'LINK',
  TEXT: 'TEXT',
  DOCUMENT: 'DOCUMENT',
});

export const UGC_MENTION_TYPES = {
  MENTIONED: 'MENTIONED',
  TAGGED: 'TAGGED',
};

export const UGC_CONTENT_RIGHTS_STATUSES = {
  NOT_REQUESTED: 'NOT_REQUESTED',
  REQUESTED: 'REQUESTED',
  APPROVED: 'APPROVED',
  REQUEST_FAILED: 'FAILED',
  LAPSED: 'LAPSED',
  EXPIRED: 'EXPIRED',
};

/** @type {Record<string, { label: string; severity: import('@dashhudson/dashing-ui/badge').BadgeSeverityProp; tooltip: string }>} */
export const CONTENT_RIGHTS_TYPE_MAP = {
  [contentRightsPostTypes.NOT_REQUESTED]: {
    label: 'Not Requested',
    severity: 'statusNeutral',
    tooltip: 'No rights request has been sent for this media',
  },
  [contentRightsPostTypes.REQUESTED]: {
    label: 'Requested',
    severity: 'info',
    tooltip: 'You sent a rights request and are waiting for the creator to respond',
  },
  [contentRightsPostTypes.APPROVED]: {
    label: 'Accepted',
    severity: 'success',
    tooltip: 'The creator accepted your rights terms',
  },
  [contentRightsPostTypes.FAILED]: {
    label: 'Request Failed',
    severity: 'danger',
    tooltip: 'This rights request failed. Open the post to see what went wrong',
  },
  [contentRightsPostTypes.LAPSED]: {
    label: 'Lapsed',
    severity: 'warning',
    tooltip: "The creator didn't respond within 14 days. Send a new request to try again",
  },
  [contentRightsPostTypes.EXPIRED]: {
    label: 'Expired',
    severity: 'statusNeutral',
    tooltip: 'Your rights have expired. Send a new request to renew them',
  },
};

/** @type {{ value: string; label: string; severity: import('@dashhudson/dashing-ui/badge').BadgeSeverityProp }[]} */
export const CONTENT_RIGHTS_OPTIONS = [
  {
    value: contentRightsPostTypes.NOT_REQUESTED,
    label: 'Not Requested',
    severity: 'statusNeutral',
  },
  { value: contentRightsPostTypes.APPROVED, label: 'Accepted', severity: 'success' },
  { value: contentRightsPostTypes.EXPIRED, label: 'Expired', severity: 'statusNeutral' },
];

const contentRightsFailureReasonCodes = {
  BRAND_NOT_MENTIONED: 'BRAND_NOT_MENTIONED',
  ACCOUNT_NOT_CONNECTED_TO_FACEBOOK: 'ACCOUNT_NOT_CONNECTED_TO_FACEBOOK',
  COMMENTS_RESTRICTED: 'COMMENTS_RESTRICTED',
  UNKNOWN: 'UNKNOWN',
};

export const CONTENT_RIGHTS_FAILURE_FALLBACK_MESSAGE =
  'Something went wrong. Try sending this request again.';

export const CONTENT_RIGHTS_FAILURE_REASON_MAP = {
  [contentRightsFailureReasonCodes.BRAND_NOT_MENTIONED]:
    "The post doesn't mention your brand in the post caption.",
  [contentRightsFailureReasonCodes.ACCOUNT_NOT_CONNECTED_TO_FACEBOOK]:
    "Your Instagram account isn't connected to a Facebook Page.",
  [contentRightsFailureReasonCodes.COMMENTS_RESTRICTED]:
    "Your comment wasn't posted due to the post's privacy, audience, spam, or comment settings.",
  [contentRightsFailureReasonCodes.UNKNOWN]: CONTENT_RIGHTS_FAILURE_FALLBACK_MESSAGE,
};

export const AUDIO_FILTER_VALUES = Object.freeze({
  NO_MUSIC: 'noMusic',
  MUSIC_DETECTED: 'musicDetected',
  MUSIC_MUTED: 'musicMuted',
});

export const LIBRARY_SCOPED_FILTERS = Object.freeze({
  GALLERIES: {
    scope: 'libraryGalleries',
    default: {
      tags: [],
      showAnalytics: 'off',
    },
  },
  PRODUCTS: {
    scope: 'libraryProducts',
    options: {
      sort: productSortOptions,
      importSources: [
        {
          formattedLabel: 'Facebook Product Catalog',
          label: 'Facebook Product Catalog',
          value: 'facebook-product-catalog',
        },
        {
          formattedLabel: 'Single Product Link',
          label: 'Single Product Link',
          value: 'single-product-link',
        },
        {
          formattedLabel: 'Other Product Feeds',
          label: 'Other Product Feeds',
          value: 'other-product-feed',
        },
      ],
      availabilities: [
        {
          label: productAvailabilityLabels.IN_STOCK,
          formattedLabel: productAvailabilityLabels.IN_STOCK,
          value: productAvailabilityTypes.IN_STOCK,
        },
        {
          label: productAvailabilityLabels.OUT_OF_STOCK,
          formattedLabel: productAvailabilityLabels.OUT_OF_STOCK,
          value: productAvailabilityTypes.OUT_OF_STOCK,
        },
        {
          label: productAvailabilityLabels.PREORDER,
          formattedLabel: productAvailabilityLabels.PREORDER,
          value: productAvailabilityTypes.PREORDER,
        },
        {
          label: productAvailabilityLabels.AVAILABLE_FOR_ORDER,
          formattedLabel: productAvailabilityLabels.AVAILABLE_FOR_ORDER,
          value: productAvailabilityTypes.AVAILABLE_FOR_ORDER,
        },
        {
          label: productAvailabilityLabels.DISCONTINUED,
          formattedLabel: productAvailabilityLabels.DISCONTINUED,
          value: productAvailabilityTypes.DISCONTINUED,
        },
      ],
    },
    default: {
      sort: {
        selected: productSortOptions[0],
        sortOrder: 'DESC',
      },
      importSources: [],
      availabilities: [],
      withMediaOnly: false,
      showArchived: false,
      showAnalytics: false,
    },
  },
  MEDIA: {
    scope: 'libraryMedia',
    options: {
      // TODO: remove mediaTypeLegacy after launching Find Media GDI
      mediaTypeLegacy: [
        { label: 'Photo', value: mediaTypes.IMAGE },
        { label: 'Video', value: mediaTypes.VIDEO },
      ],
      mediaType: [
        { label: 'Audio', value: PLATFORM_MEDIA_TYPES.AUDIO },
        { label: 'Document', value: PLATFORM_MEDIA_TYPES.DOCUMENT },
        { label: 'Image/Photo', value: PLATFORM_MEDIA_TYPES.IMAGE },
        { label: 'Instagram Story', value: PLATFORM_MEDIA_TYPES.STORY },
        { label: 'Link', value: PLATFORM_MEDIA_TYPES.LINK },
        { label: 'Text', value: PLATFORM_MEDIA_TYPES.TEXT },
        { label: 'Video', value: PLATFORM_MEDIA_TYPES.VIDEO },
      ],
      performance: PREDICTION_TIER_OPTIONS,
      color: [
        { label: 'Red', value: 'red' },
        { label: 'Orange', value: 'orange' },
        { label: 'Yellow', value: 'yellow' },
        { label: 'Green', value: 'green' },
        { label: 'Teal', value: 'cyan' },
        { label: 'Blue', value: 'blue' },
        { label: 'Purple', value: 'purple' },
        { label: 'Pink', value: 'pink' },
        { label: 'Brown', value: 'brown' },
        { label: 'Black', value: 'black' },
        { label: 'Grey', value: 'gray' },
        { label: 'White', value: 'white' },
      ],
      size: [
        { label: '> 800 x 600', value: '800x600' },
        { label: '> 1024 x 768', value: '1024x768' },
        { label: '> 1600 x 1200', value: '1600x1200' },
      ],
    },
    default: {
      sources: [],
      channels: [],
      mediaTypes: [],
      contentTags: [],
      galleries: [],
      contentTagMatchType: DEFAULT_CONTENT_TAG_MATCH_TYPE,
      dateRange: {
        preset: 'ALL_TIME',
        presetObject: {
          value: 'ALL_TIME',
          label: 'All Time',
        },
        range: undefined,
      },
      contentRights: 'off',
      showDuplicateMedia: 'off',
      aspectRatio: [],
      hasNotes: 'off',
      colors: [],
      performance: [],
      showVisionAIMedia: false,

      // Instagram Collaborators filter
      authorship: [],
      collaboratorUsernames: [],
    },
    queryParamMapping: {
      contentTags: { queryParamName: 'contentTags', type: 'array' },
      contentTagMatchType: { queryParamName: 'contentTagMatchType', type: 'string' },
      sources: { queryParamName: 'sources', type: 'array' },
      channels: { queryParamName: 'channels', type: 'array' },
      mediaTypes: { queryParamName: 'mediaTypes', type: 'array' },
      'dateRange.range.start': { queryParamName: 'startDate', type: 'date' },
      'dateRange.range.end': { queryParamName: 'endDate', type: 'date' },
      'dateRange.preset': { queryParamName: 'dateRangeMode', type: 'string' },
      contentRights: { queryParamName: 'rightsStatus', type: 'string' },
      aspectRatio: { queryParamName: 'aspectRatios', type: 'array' },
      hasNotes: { queryParamName: 'hasNotes', type: 'string' },
      colors: { queryParamName: 'colors', type: 'array' },
      showDuplicateMedia: { queryParamName: 'showDuplicateMedia', type: 'string' },
      size: { queryParamName: 'size', type: 'string' },
      performance: { queryParamName: 'performance', type: 'array' },
      showVisionAIMedia: { queryParamName: 'showVisionAIMedia', type: 'boolean' },
      authorship: { queryParamName: 'authorship', type: 'array' },
      collaboratorUsernames: { queryParamName: 'collaboratorUsernames', type: 'array' },
    },
  },
  UGC: {
    scope: 'libraryUgc',
    options: {
      channels: [
        { label: 'Instagram', value: 'INSTAGRAM', icon: 'instagram' },
        { label: 'TikTok', value: 'TIKTOK', icon: 'tiktok' },
      ],
      contentRightsType: [
        {
          label: 'Not Requested',
          value: UGC_CONTENT_RIGHTS_STATUSES.NOT_REQUESTED,
          color: 'var(--color-status-neutral)',
          tooltipIcon: 'No rights request has been sent for this media',
        },
        {
          label: 'Requested',
          value: UGC_CONTENT_RIGHTS_STATUSES.REQUESTED,
          color: 'var(--color-status-in-progress)',
          tooltipIcon: 'You sent a rights request and are waiting for the creator to respond',
        },
        {
          label: 'Accepted',
          value: UGC_CONTENT_RIGHTS_STATUSES.APPROVED,
          color: 'var(--color-status-success)',
          tooltipIcon: 'The creator accepted your rights terms',
        },
        {
          label: 'Request Failed',
          value: UGC_CONTENT_RIGHTS_STATUSES.REQUEST_FAILED,
          color: 'var(--color-status-danger)',
          tooltipIcon: 'This rights request failed. Open the post to see what went wrong',
        },
        {
          label: 'Lapsed',
          value: UGC_CONTENT_RIGHTS_STATUSES.LAPSED,
          color: 'var(--color-status-warning)',
          tooltipIcon: "The creator didn't respond within 14 days. Send a new request to try again",
        },
        {
          label: 'Expired',
          value: UGC_CONTENT_RIGHTS_STATUSES.EXPIRED,
          color: 'var(--color-status-neutral)',
          tooltipIcon: 'Your rights have expired. Send a new request to renew them',
        },
      ],
      postType: [
        { label: 'Post', value: 'POST' },
        { label: 'Reel', value: 'REEL' },
        { label: 'Story', value: 'STORY' },
      ],
      mentionType: [
        {
          label: 'Photo-tagged',
          value: UGC_MENTION_TYPES.TAGGED,
          icon: 'photo-tagged',
          tooltipIcon: 'Posts where your brand was tagged in the photo',
        },
        {
          label: 'Mentioned',
          value: UGC_MENTION_TYPES.MENTIONED,
          icon: 'at-sign',
          tooltipIcon: 'Posts where your brand was @mentioned in the caption',
        },
      ],
      performance: PREDICTION_TIER_OPTIONS,
      music: [
        { value: AUDIO_FILTER_VALUES.NO_MUSIC, label: 'No Music' },
        {
          value: AUDIO_FILTER_VALUES.MUSIC_DETECTED,
          label: 'Music Detected',
          tooltipIcon: 'Filter Instagram UGC where music has been detected in the post',
        },
        {
          value: AUDIO_FILTER_VALUES.MUSIC_MUTED,
          label: 'Music Muted by Dash',
          tooltipIcon: 'Filter Instagram UGC where music in the post has been detected and muted',
        },
      ],
    },
    default: {
      music: [],
      showHiddenUgc: 'off',
      dateRange: {
        preset: 'ALL_TIME',
        presetObject: {
          value: 'ALL_TIME',
          label: 'All Time',
        },
        range: undefined,
      },
      channels: [],
      postTypes: [],
      mentionTypes: [],
      contentRightsType: [],
      performance: [],
      contentRightsRequestedDate: {
        preset: 'ALL_TIME',
        presetObject: {
          value: 'ALL_TIME',
          label: 'All Time',
        },
        range: undefined,
      },
      contentTags: [],
      contentTagMatchType: DEFAULT_CONTENT_TAG_MATCH_TYPE,
      requestedBy: undefined,
    },
    queryParamMapping: {
      'dateRange.range.start': { queryParamName: 'startDate', type: 'date' },
      'dateRange.range.end': { queryParamName: 'endDate', type: 'date' },
      'dateRange.preset': { queryParamName: 'dateRangeMode', type: 'string' },
      channels: { queryParamName: 'channels', type: 'array' },
      postTypes: { queryParamName: 'postTypes', type: 'array' },
      mentionTypes: { queryParamName: 'mentionTypes', type: 'array' },
      contentRightsType: { queryParamName: 'contentRightsType', type: 'array' },
      performance: { queryParamName: 'performance', type: 'array' },
      'contentRightsRequestedDate.range.start': {
        queryParamName: 'rightsRequestedStartDate',
        type: 'date',
      },
      'contentRightsRequestedDate.range.end': {
        queryParamName: 'rightsRequestedEndDate',
        type: 'date',
      },
      contentTags: { queryParamName: 'contentTags', type: 'array' },
      contentTagMatchType: { queryParamName: 'contentTagMatchType', type: 'string' },
      requestedBy: { queryParamName: 'requestedBy', type: 'string' },
      music: { queryParamName: 'music', type: 'array' },
    },
  },
  SELECT_MEDIA: {
    scope: 'selectMediaDrawer',
  },
  CONSTELLATION_MEDIA: {
    scope: 'constellationProMedia',
  },
});

export const FILTER_SECTIONS = Object.freeze({
  SOURCES: 'sources',
  CHANNELS: 'channels',
  MEDIA_TYPES: 'mediaTypes',
  GALLERIES: 'galleries',
  CONTENT_TAGS: 'contentTags',
  DATE_ADDED: 'dateAdded',
  RIGHTS: 'rights',
  SIMILAR_IMAGES: 'similarImages',
  ASPECT_RATIO: 'aspectRatio',
  NOTES: 'notes',
  COLOR: 'color',
  DUPLICATE_MEDIA: 'duplicateMedia',
  MEDIA_SIZE: 'mediaSize',
  PREDICTED_PERFORMANCE: 'predictedPerformance',
  BRAND: 'brand',
});

export const ALL_FILTERS = Object.freeze(Object.values(FILTER_SECTIONS));

export const STANDARD_FILTERS = Object.freeze([
  FILTER_SECTIONS.SOURCES,
  FILTER_SECTIONS.CHANNELS,
  FILTER_SECTIONS.MEDIA_TYPES,
  FILTER_SECTIONS.GALLERIES,
  FILTER_SECTIONS.DATE_ADDED,
  FILTER_SECTIONS.CONTENT_TAGS,
]);

export const LIBRARY_MEDIA_FILTER_TOOLTIPS = Object.freeze({
  DUPLICATE_MEDIA: 'Show or hide duplicate media and posts',
  PREDICTED_PERFORMANCE:
    'Vision AI predicts the performance of UGC and uploads based on the past performance of your channel',
  REPORTING_PERIOD: 'Includes posts published or uploaded within the reporting period',
  CONTENT_TAGS:
    'Show only posts that have Content Tags applied. Match Any includes posts with any selected tag. Match All includes only posts with every selected tag.',
  GALLERIES: 'Filter by media that appears in the selected Galleries',
});

export const SENTIMENTS = Object.freeze({
  POSITIVE: 'POSITIVE',
  NEUTRAL: 'NEUTRAL',
  NEGATIVE: 'NEGATIVE',
});

export const SENTIMENT_DISALLOWED_CHANNELS = Object.freeze([
  'instagram_story_frame',
  'linkedin',
  'pinterest',
  'threads',
  'snapchat',
]);

export const SENTIMENT_SOURCE_TYPES = Object.freeze(['OWNED', 'UGC']);

export const ADD_TO_GALLERY_DISABLED_TOOLTIPS = Object.freeze({
  CONTAINS_YOUTUBE_UGC: 'Adding YouTube UGC to Boards is not available.',
  CONTAINS_YOUTUBE_UGC_GALLERY: 'Adding YouTube UGC to Galleries is not available.',
  INSTAGRAM_STORY: 'You can only add Instagram Stories to Instagram Story Boards.',
  INSTAGRAM: 'You can only add Instagram posts to Instagram Boards.',
  PINTEREST: 'You can only add Pinterest pins to Pinterest Boards.',
  TIKTOK: 'You can only add TikTok posts to TikTok Boards.',
  YOUTUBE: 'You can only add YouTube videos to YouTube Boards.',
  CONTAINS_TEXT_POSTS: 'Posts without media cannot be added to a Gallery.',
  CONTAINS_NON_ORIGINAL_POST: 'Reposts, Quote Posts, and Replies cannot be added to a Board.',
  X: 'You can only add X posts to X Boards.',
  FACEBOOK: 'You can only add Facebook posts to Facebook Boards.',
  LINKEDIN: 'You can only add LinkedIn posts to LinkedIn Boards.',
  THREADS: 'You can only add Threads posts to Threads Boards.',
  SNAPCHAT: 'You can only add Snapchat posts to Snapchat Boards.',
  AI_GENERATION: 'You can only add images or AI generated media to Constellation Projects.',
});

export const CONSTELLATION_EXCLUDED_SOURCES = Object.freeze(['TIKTOK', 'TIKTOK_AD', 'YOUTUBE']);

// Mirrors the backend's BULK_DOWNLOAD_DISALLOWED_SOURCES (library-backend
// workers/media_download.py). Bulk download supports images and videos from any
// source except these.
export const BULK_DOWNLOAD_EXCLUDED_SOURCES = Object.freeze(['TIKTOK', 'TIKTOK_AD', 'YOUTUBE']);

export const ADD_TO_CAMPAIGN_DISABLED_TOOLTIP =
  'Some or all of the items you have selected are not supported in Campaigns.';

export const PLATFORM_MEDIA_TYPES_MAP = {
  SHORTS: PLATFORM_MEDIA_TYPES.SHORT,
  PHOTO: PLATFORM_MEDIA_TYPES.IMAGE,
  REELS: PLATFORM_MEDIA_TYPES.REEL,
  STATUS: PLATFORM_MEDIA_TYPES.TEXT,
};

export const SNAPCHAT_MEDIA_TYPES = {
  STORY: 'SNAPCHAT_STORY',
  SPOTLIGHT: 'SNAPCHAT_SPOTLIGHT',
  SAVED_STORY: 'SNAPCHAT_SAVED_STORY',
};

export const LIBRARY_EMPTY_STATE_IMAGES = {
  CONNECT_ACCOUNTS: {
    BANNER_SRC: emptyLibraryConnectAccounts1x,
    BANNER_SRC_SET: srcSet(
      emptyLibraryConnectAccounts1x,
      emptyLibraryConnectAccounts2x,
      emptyLibraryConnectAccounts3x,
    ),
  },
  UPLOAD_MEDIA: {
    BANNER_SRC: emptyLibraryUploadMedia1x,
    BANNER_SRC_SET: srcSet(
      emptyLibraryUploadMedia1x,
      emptyLibraryUploadMedia2x,
      emptyLibraryUploadMedia3x,
    ),
  },
};

export const MEDIA_GRID_EMPTY_STATE = Object.freeze({
  variant: 'sectionEmpty',
  icon: 'search',
  title: "Couldn't Find a Match",
  message: 'Review or adjust your filters or search to uncover more results',
  // Nested `Object.freeze` keeps `severity` a string literal (assignable to ButtonSeverity)
  // instead of widening to `string`, so this satisfies `ContentBannerProps.ctaButton`.
  ctaButton: Object.freeze({
    label: 'Reset All',
    severity: 'tertiary',
    'data-exclude-side-panel-close': true,
  }),
});

export const UGC_NO_CONTENT_EMPTY_STATE = Object.freeze({
  variant: 'pageEmpty',
  title: 'Collect Your First UGC',
  message: 'Media where your brand has been tagged or mentioned will appear here',
  center: true,
});

export const HIDDEN_UGC_EMPTY_STATE = Object.freeze({
  variant: 'pageEmpty',
  title: 'No hidden content found',
  message: 'All UGC content is visible',
  center: true,
});

export const MEDIA_GRID_LIBRARY_CARD_PRESETS = Object.freeze({
  sm: 'LibraryCardSmall',
  md: 'LibraryCardMedium',
  lg: 'LibraryCardLarge',
});

export const CONVERTED_VIDEO_DURATION_WARNING =
  'There may be an issue with the length of this video. It was processed successfully but please review to ensure it plays as expected.';
export const CONVERTED_VIDEO_DURATION_WARNING_SOME_MEDIA =
  'There may be an issue with the length of one of your videos. They were processed successfully but please review to ensure they play as expected.';

const META_ADS_EFFECTIVE_STATUS = {
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  DELETED: 'DELETED',
  PENDING_REVIEW: 'PENDING_REVIEW',
  DISAPPROVED: 'DISAPPROVED',
  PREAPPROVED: 'PREAPPROVED',
  PENDING_BILLING_INFO: 'PENDING_BILLING_INFO',
  CAMPAIGN_PAUSED: 'CAMPAIGN_PAUSED',
  ARCHIVED: 'ARCHIVED',
  ADSET_PAUSED: 'ADSET_PAUSED',
  IN_PROCESS: 'IN_PROCESS',
  WITH_ISSUES: 'WITH_ISSUES',
};

export const META_AD_CAMPAIGNS_FILTER_SCOPE = 'meta-ad-campaigns';

export const META_ADS_CAMPAIGN_DETAIL_FILTER_SCOPE = 'meta-ads-campaign-detail';

export const META_ADS_CAMPAIGNS_EFFECTIVE_STATUS_OPTIONS = [
  { label: 'Active', value: META_ADS_EFFECTIVE_STATUS.ACTIVE },
  { label: 'Paused', value: META_ADS_EFFECTIVE_STATUS.PAUSED },
  { label: 'Archived', value: META_ADS_EFFECTIVE_STATUS.ARCHIVED },
];

export const META_ADS_CAMPAIGNS_SORT_OPTIONS = [
  { label: 'Amount Spent', value: 'SPEND' },
  { label: 'Clicks (All)', value: 'CLICKS' },
  { label: 'Conversion Rate', value: 'WEB_CONVERSION_RATE' },
  { label: 'CPC', value: 'CPC' },
  { label: 'CPM', value: 'CPM' },
  { label: 'CTR', value: 'CTR' },
  { label: 'Date Created', value: 'SOURCE_CREATED_TIME' },
  { label: 'Engagement Rate', value: 'ENGAGEMENT_RATE' },
  { label: 'Engagements', value: 'POST_ENGAGEMENT' },
  { label: 'Frequency', value: 'FREQUENCY' },
  { label: 'Impressions', value: 'IMPRESSIONS' },
  { label: 'Link Clicks', value: 'LINK_CLICKS' },
  { label: 'Number of Ads', value: 'NUM_ADS' },
  { label: 'Page Engagements', value: 'PAGE_ENGAGEMENT' },
  { label: 'Reach', value: 'REACH' },
  { label: 'ROAS', value: 'PURCHASE_ROAS' },
  { label: 'Video Views', value: 'VIDEO_VIEWS' },
  { label: 'Web Conversions', value: 'WEB_CONVERSIONS' },
];

export const META_ADS_CAMPAIGNS_DEFAULT_CARD_METRICS = [
  {
    label: 'CTR',
    sortValue: 'CTR',
    formatType: 'percent',
    attribute: 'ctr',
    normalizePercent: true,
    formatOptions: { formatOptions: { minimumFractionDigits: 2, maximumFractionDigits: 2 } },
  },
  { label: 'ROAS', sortValue: 'PURCHASE_ROAS', formatType: 'number', attribute: 'purchaseRoas' },
  { label: 'CPC', sortValue: 'CPC', formatType: 'currency', attribute: 'cpc' },
  {
    label: 'Conversion Rate',
    sortValue: 'WEB_CONVERSION_RATE',
    formatType: 'percent',
    attribute: 'webConversionRate',
  },
];

export const META_ADS_CAMPAIGNS_ALL_METRICS = {
  SPEND: { label: 'Amount Spent', formatType: 'currency', attribute: 'spend' },
  CLICKS: { label: 'Clicks (All)', formatType: 'number', attribute: 'clicks' },
  WEB_CONVERSION_RATE: {
    label: 'Conversion Rate',
    formatType: 'percent',
    attribute: 'webConversionRate',
  },
  CPC: { label: 'CPC', formatType: 'currency', attribute: 'cpc' },
  CPM: { label: 'CPM', formatType: 'currency', attribute: 'cpm' },
  CTR: {
    label: 'CTR',
    formatType: 'percent',
    attribute: 'ctr',
    normalizePercent: true,
    formatOptions: { formatOptions: { minimumFractionDigits: 2, maximumFractionDigits: 2 } },
  },
  SOURCE_CREATED_TIME: {
    label: 'Date Created',
    formatType: 'date',
    attribute: 'sourceCreatedTime',
    hideOnCard: true,
  },
  ENGAGEMENT_RATE: { label: 'Engagement Rate', formatType: 'percent', attribute: 'engagementRate' },
  POST_ENGAGEMENT: { label: 'Engagements', formatType: 'number', attribute: 'postEngagement' },
  FREQUENCY: { label: 'Frequency', formatType: 'number', attribute: 'frequency' },
  IMPRESSIONS: { label: 'Impressions', formatType: 'number', attribute: 'impressions' },
  LINK_CLICKS: { label: 'Link Clicks', formatType: 'number', attribute: 'linkClicks' },
  PAGE_ENGAGEMENT: { label: 'Page Engagements', formatType: 'number', attribute: 'pageEngagement' },
  REACH: { label: 'Reach', formatType: 'number', attribute: 'reach' },
  PURCHASE_ROAS: { label: 'ROAS', formatType: 'number', attribute: 'purchaseRoas' },
  VIDEO_VIEWS: { label: 'Video Views', formatType: 'number', attribute: 'videoViews' },
  WEB_CONVERSIONS: { label: 'Web Conversions', formatType: 'number', attribute: 'webConversions' },
  NUM_ADS: { label: 'Number of Ads', formatType: 'number', attribute: 'numAds' },
};

export const MEDIA_EDIT_STANDARD_SIZE_OPTIONS = [
  { label: 'Free Crop', value: 'freeCrop', ratio: null },
  { label: 'Original', value: 'original', ratio: null },
];

export const MEDIA_EDIT_ASPECT_RATIO_OPTIONS = {
  INSTAGRAM: [
    { label: 'Carousel 4:5', value: 'instagramCarousel', ratio: '4:5' },
    { label: 'Feed Ad 4:5', value: 'instagramFeedAd', ratio: '4:5' },
    { label: 'Landscape Post 1.91:1', value: 'instagramLandscape', ratio: '1.91:1' },
    { label: 'Portrait Post 4:5', value: 'instagramPortrait', ratio: '4:5' },
    { label: 'Reel 9:16', value: 'instagramReel', ratio: '9:16' },
    { label: 'Reel Ad 9:16', value: 'instagramReelAd', ratio: '9:16' },
    { label: 'Square Post 1:1', value: 'instagramSquare', ratio: '1:1' },
    { label: 'Story 9:16', value: 'instagramStory', ratio: '9:16' },
  ],
  FACEBOOK: [
    { label: 'Landscape Post 1.91:1', value: 'facebookLandscape', ratio: '1.91:1' },
    { label: 'Square Post 1:1', value: 'facebookSquare', ratio: '1:1' },
    { label: 'Story 9:16', value: 'facebookStory', ratio: '9:16' },
    { label: 'Reel 9:16', value: 'facebookReel', ratio: '9:16' },
    { label: 'Vertical Post 4:5', value: 'facebookVertical', ratio: '4:5' },
  ],
  PINTEREST: [
    { label: 'Square Pin 1:1', value: 'pinterestSquare', ratio: '1:1' },
    { label: 'Standard Pin 2:3', value: 'pinterestStandard', ratio: '2:3' },
    { label: 'Vertical Pin 9:16', value: 'pinterestVertical', ratio: '9:16' },
  ],
  LINKEDIN: [
    { label: 'Shared Link 1.91:1', value: 'linkedinSharedLink', ratio: '1.91:1' },
    { label: 'Square Post 1:1', value: 'linkedinSquare', ratio: '1:1' },
    { label: 'Vertical Post 4:5', value: 'linkedinVertical', ratio: '4:5' },
  ],
  TWITTER: [
    { label: 'Landscape Post 16:9', value: 'twitterLandscape', ratio: '16:9' },
    { label: 'Link Card 1.91:1', value: 'twitterLinkCard', ratio: '1.91:1' },
    { label: 'Square Post 1:1', value: 'twitterSquare', ratio: '1:1' },
  ],
};
